import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Textarea from '../../../components/ui/textarea';
import { getIncidentWorkflowLabel } from '../../../lib/incidentWorkflow';

const IncidentsTab = ({
  incidents,
  onReportIncident,
  onIncidentStatusChange,
  onDownloadAttachment,
  userRole,
  roleTypes,
  canTreatIncidents,
}) => {
  const [showReportForm, setShowReportForm] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    severity: 'medium',
    category: 'quality',
  });

  /** Formulaire passage de statut (message obligatoire) */
  const [transitionDraft, setTransitionDraft] = useState(null);
  /** { incidentId, nextStatus: 'in_progress' | 'resolved' } */
  const [transitionMessage, setTransitionMessage] = useState('');

  const severityOptions = [
    { value: 'low', label: 'Faible' },
    { value: 'medium', label: 'Moyen' },
    { value: 'high', label: 'Élevé' },
    { value: 'critical', label: 'Critique' },
  ];

  const categoryOptions = [
    { value: 'quality', label: 'Problème de qualité' },
    { value: 'delivery', label: 'Problème de livraison' },
    { value: 'communication', label: 'Problème de communication' },
    { value: 'technical', label: 'Problème technique' },
    { value: 'other', label: 'Autre' },
  ];

  const categoryLabelMap = Object.fromEntries(categoryOptions.map((o) => [o.value, o.label]));

  const getSeverityColor = (severity) => {
    const colors = {
      low: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      medium: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
      high: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
      critical: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
    };
    return colors?.[severity] || colors?.medium;
  };

  const getStatusColor = (status) => {
    const colors = {
      open: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
      in_progress: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
      resolved: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    };
    return colors?.[status] || colors?.open;
  };

  const types = Array.isArray(roleTypes) ? roleTypes : [];
  const isAdmin = types.includes('administrator');
  const isDentistSide = types.includes('dentist_client') || types.includes('dentist_supplier');
  const hasNonClosedIncident = incidents?.some((i) => i?.status !== 'resolved');

  const baseCanReport =
    userRole === 'dentist_client' || userRole === 'administrator' || userRole === 'dentist_supplier';

  const dentistBlockedFromNewIncident = !isAdmin && isDentistSide && hasNonClosedIncident;

  const canReport = baseCanReport && !dentistBlockedFromNewIncident;

  const handleSubmit = () => {
    onReportIncident(formData);
    setFormData({ title: '', description: '', severity: 'medium', category: 'quality' });
    setShowReportForm(false);
  };

  const openTransitionForm = (incidentId, nextStatus) => {
    setTransitionDraft({ incidentId, nextStatus });
    setTransitionMessage('');
  };

  const cancelTransitionForm = () => {
    setTransitionDraft(null);
    setTransitionMessage('');
  };

  const submitTransition = async () => {
    if (!transitionDraft || !onIncidentStatusChange) return;
    const msg = transitionMessage.trim();
    if (!msg) return;
    try {
      await onIncidentStatusChange(transitionDraft.incidentId, transitionDraft.nextStatus, msg);
      cancelTransitionForm();
    } catch {
      // toast affiché dans la fiche dossier
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-semibold text-foreground">Incidents & Difficultés</h2>
          {incidents?.filter((i) => i?.status !== 'resolved')?.length > 0 && (
            <span className="notification-badge urgent">
              {incidents?.filter((i) => i?.status !== 'resolved')?.length}
            </span>
          )}
        </div>
        {canReport && !showReportForm && (
          <Button
            type="button"
            variant="default"
            iconName="AlertCircle"
            iconPosition="left"
            onClick={() => setShowReportForm(true)}
          >
            Signaler un incident
          </Button>
        )}
      </div>

      {dentistBlockedFromNewIncident && (
        <p className="text-sm text-muted-foreground border border-border rounded-lg p-3 bg-muted/30">
          Un incident est déjà ouvert ou en cours de traitement sur ce dossier. Il sera à nouveau possible
          d’en signaler un nouveau une fois celui-ci <strong className="text-foreground">fermé</strong>.
        </p>
      )}

      {showReportForm && (
        <div className="bg-card border border-border rounded-lg p-6">
          <h3 className="text-base font-semibold text-foreground mb-4">Signaler un nouvel incident</h3>
          <div className="space-y-4">
            <Input
              label="Titre de l'incident"
              type="text"
              placeholder="Brève description du problème"
              value={formData?.title}
              onChange={(e) => setFormData({ ...formData, title: e?.target?.value })}
              required
            />
            <Input
              label="Description détaillée"
              type="text"
              placeholder="Fournissez des informations détaillées sur l'incident"
              value={formData?.description}
              onChange={(e) => setFormData({ ...formData, description: e?.target?.value })}
              required
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Select
                label="Niveau de gravité"
                options={severityOptions}
                value={formData?.severity}
                onChange={(value) => setFormData({ ...formData, severity: value })}
              />
              <Select
                label="Catégorie"
                options={categoryOptions}
                value={formData?.category}
                onChange={(value) => setFormData({ ...formData, category: value })}
              />
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => setShowReportForm(false)}>
                Annuler
              </Button>
              <Button
                type="button"
                variant="default"
                iconName="Send"
                iconPosition="left"
                onClick={handleSubmit}
                disabled={!formData?.title || !formData?.description}
              >
                Soumettre le rapport
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-4">
        {incidents?.map((incident) => (
          <div key={incident?.id} className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <h3 className="text-base font-semibold text-foreground">{incident?.title}</h3>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getSeverityColor(incident?.severity)}`}>
                    {incident?.severityLabel}
                  </span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(incident?.status)}`}>
                    {incident?.statusLabel || getIncidentWorkflowLabel(incident?.status)}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground mb-2">{incident?.description}</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-3">
              <div className="flex items-center gap-2">
                <Icon name="Tag" size={16} className="text-muted-foreground" />
                <div>
                  <p className="text-xs text-muted-foreground">Catégorie</p>
                  <p className="text-sm font-medium text-foreground">
                    {categoryLabelMap[incident?.category] || incident?.categoryLabel}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Icon name="User" size={16} className="text-muted-foreground" />
                <div>
                  <p className="text-xs text-muted-foreground">Signalé par</p>
                  <p className="text-sm font-medium text-foreground">{incident?.reportedBy}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Icon name="Calendar" size={16} className="text-muted-foreground" />
                <div>
                  <p className="text-xs text-muted-foreground">Date de signalement</p>
                  <p className="text-sm font-medium text-foreground">{incident?.reportedDate}</p>
                </div>
              </div>
              {incident?.resolvedDate && (
                <div className="flex items-center gap-2">
                  <Icon name="CheckCircle" size={16} className="text-success" />
                  <div>
                    <p className="text-xs text-muted-foreground">Date de fermeture</p>
                    <p className="text-sm font-medium text-foreground">{incident?.resolvedDate}</p>
                  </div>
                </div>
              )}
            </div>

            {incident?.resolution && (
              <div className="p-3 bg-success/10 rounded-lg mb-3">
                <p className="text-sm font-medium text-success mb-1">Dernière clôture (synthèse)</p>
                <p className="text-sm text-foreground">{incident?.resolution}</p>
              </div>
            )}

            {incident?.attachment && (
              <div className="mb-3">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  iconName="Paperclip"
                  iconPosition="left"
                  onClick={() => onDownloadAttachment?.(incident)}
                >
                  Pièce jointe: {incident.attachment.name}
                </Button>
              </div>
            )}

            {canTreatIncidents && incident?.status === 'open' && (
              <div className="mb-4 space-y-2">
                {!transitionDraft || transitionDraft.incidentId !== incident.id ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    iconName="PlayCircle"
                    iconPosition="left"
                    onClick={() => openTransitionForm(incident.id, 'in_progress')}
                  >
                    Passer en cours de traitement
                  </Button>
                ) : null}
              </div>
            )}

            {canTreatIncidents && incident?.status === 'in_progress' && (
              <div className="mb-4 space-y-2">
                {!transitionDraft || transitionDraft.incidentId !== incident.id ? (
                  <Button
                    type="button"
                    variant="default"
                    size="sm"
                    iconName="CheckCircle"
                    iconPosition="left"
                    onClick={() => openTransitionForm(incident.id, 'resolved')}
                  >
                    Fermer l’incident
                  </Button>
                ) : null}
              </div>
            )}

            {transitionDraft?.incidentId === incident.id && (
              <div className="mb-4 space-y-2 rounded-lg border border-border p-3 bg-muted/20">
                <p className="text-sm font-medium text-foreground">
                  {transitionDraft.nextStatus === 'in_progress'
                    ? 'Passage en cours de traitement'
                    : 'Clôture de l’incident'}
                </p>
                <label className="text-xs font-medium text-muted-foreground">
                  Décrivez ce qui a été fait (obligatoire)
                </label>
                <Textarea
                  value={transitionMessage}
                  onChange={(e) => setTransitionMessage(e.target.value)}
                  rows={4}
                  placeholder="Message pour l’historique du dossier…"
                />
                <div className="flex gap-2 pt-2">
                  <Button type="button" variant="outline" size="sm" onClick={cancelTransitionForm}>
                    Annuler
                  </Button>
                  <Button
                    type="button"
                    variant="default"
                    size="sm"
                    disabled={!transitionMessage.trim()}
                    onClick={() => submitTransition()}
                  >
                    Confirmer
                  </Button>
                </div>
              </div>
            )}

            {Array.isArray(incident?.activities) && incident.activities.length > 0 && (
              <div className="mt-2 border-t border-border pt-3">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                  Historique de traitement
                </p>
                <ul className="space-y-2">
                  {incident.activities.map((a) => (
                    <li key={a.id} className="rounded-md bg-muted/30 px-3 py-2 text-sm">
                      <p className="text-xs text-muted-foreground">
                        {a.dateLabel} · {a.actorName}
                      </p>
                      <p className="font-medium text-foreground">
                        {a.fromLabel} → {a.toLabel}
                      </p>
                      <p className="text-foreground mt-1 whitespace-pre-wrap">{a.message}</p>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ))}
      </div>

      {incidents?.length === 0 && (
        <div className="text-center py-12">
          <Icon name="CheckCircle" size={48} className="text-success mx-auto mb-4" />
          <p className="text-muted-foreground">Aucun incident signalé pour ce dossier</p>
        </div>
      )}
    </div>
  );
};

export default IncidentsTab;
